import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, FileText, Image as ImageIcon, Send, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { api, fmtUSD } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { EmptyState } from '@/components/Primitives';
import { SecurityBanner } from '@/components/SecurityBanner';
import { UserBadges } from '@/components/TrustBadge';

const initials = (u) => `${(u?.first_name || '?')[0]}${(u?.last_name || '')[0] || ''}`.toUpperCase();
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

export const MessagesListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/conversations').then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
      <h1 className="text-2xl font-bold mb-4">Messages</h1>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState title="Aucune conversation" description="Vos échanges apparaîtront ici." testid="empty-conversations" />
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const other = c.participants?.find((p) => p.id !== user?.id);
            return (
              <Link key={c.id} to={`/messages/${c.id}`} data-testid={`conv-item-${c.id}`}>
                <Card className="p-3 hover:border-[#C7E2FF] hover:shadow-md">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      {other?.photo_base64 && <AvatarImage src={other.photo_base64} alt="" />}
                      <AvatarFallback className="bg-[#EAF3FF] text-[#0E3F80]">{initials(other)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-[#0B1220] truncate">{other?.first_name} {other?.last_name}</p>
                        {c.unread > 0 && <span className="text-[10px] bg-[#1677F2] text-white rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">{c.unread}</span>}
                      </div>
                      <p className="text-sm text-slate-600 truncate">{c.last_message?.text || 'Commencer la conversation…'}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ConversationPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const endRef = useRef(null);

  const loadAll = async () => {
    const [cList, mList, qList] = await Promise.all([
      api.get('/conversations'),
      api.get(`/conversations/${id}/messages`),
      api.get(`/quotes/conversation/${id}`),
    ]);
    const c = (cList.data.items || []).find((x) => x.id === id);
    setConv(c);
    setMsgs(mList.data.items || []);
    setQuotes(qList.data.items || []);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim() && !photo) return;
    try {
      await api.post(`/conversations/${id}/messages`, { conversation_id: id, text: text.trim() || null, photo_base64: photo });
      setText('');
      setPhoto(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d’envoi');
    }
  };

  const urgent = async () => {
    await api.post(`/conversations/${id}/urgent?reason=Signalement%20urgent%20depuis%20le%20chat`);
    toast.success('Alerte envoyée à l’équipe Job Kongo');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  if (loading) return <div className="p-6">Chargement…</div>;
  if (!conv) return <div className="p-6">Conversation introuvable.</div>;
  const other = conv.participants?.find((p) => p.id !== user?.id);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-4 flex flex-col h-[calc(100vh-56px)] md:h-[calc(100vh-56px-80px)]" data-testid="conversation-page">
      {/* Header */}
      <div className="flex items-center gap-3 py-2 border-b border-[#E2E8F0]">
        <Avatar className="h-10 w-10">
          {other?.photo_base64 && <AvatarImage src={other.photo_base64} alt="" />}
          <AvatarFallback className="bg-[#EAF3FF] text-[#0E3F80]">{initials(other)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <Link to={`/jobeur/${other?.id}`} className="font-semibold hover:text-[#1677F2]">{other?.first_name} {other?.last_name}</Link>
          <UserBadges user={{}} />
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="text-[#CE1021] border-[#FFD7DB] hover:bg-[#FFE1E4]" data-testid="chat-urgent-button">
              <ShieldAlert size={14} className="mr-1" /> Urgence
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Signaler une urgence</AlertDialogTitle>
              <AlertDialogDescription>
                Alerte l&apos;équipe Job Kongo en cas de problème. Utilisez ce bouton uniquement pour les situations graves (fraude, menace, paiement en dehors, etc.).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={urgent} className="bg-[#CE1021] hover:bg-[#A10B18]">Envoyer l&apos;alerte</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="py-2"><SecurityBanner variant="compact" /></div>

      {/* Quote shortcuts */}
      <div className="flex flex-wrap gap-2 mb-2">
        {user?.role === 'jobeur' && (
          <Button size="sm" onClick={() => nav(`/quote/new?conversation_id=${id}`)} data-testid="chat-create-quote-btn">
            <FileText size={14} className="mr-1" /> Émettre un devis
          </Button>
        )}
        {quotes.map((q) => (
          <Button key={q.id} size="sm" variant="outline" onClick={() => nav(`/quote/${q.id}`)} data-testid={`chat-quote-${q.id}`}>
            <FileText size={14} className="mr-1" /> Devis: {fmtUSD(q.breakdown?.client_total)} <span className="ml-2 text-xs text-slate-500">{q.status}</span>
          </Button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-2 py-2" data-testid="messages-scroll">
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] px-3 py-2 ${mine ? 'bubble-mine' : 'bubble-theirs'}`}>
                {m.photo_base64 && <img src={m.photo_base64} alt="" className="rounded-lg mb-1 max-h-60" />}
                {m.text && <p className="text-sm whitespace-pre-wrap">{m.text}</p>}
                {m.quote_id && (
                  <Link to={`/quote/${m.quote_id}`} className={`block text-xs mt-1 underline ${mine ? 'text-white/90' : 'text-[#1677F2]'}`} data-testid="message-quote-link">
                    Voir le devis →
                  </Link>
                )}
                <p className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-slate-500'}`}>{fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {photo && (
        <div className="mb-2 flex items-center gap-2 bg-[#EAF3FF] px-3 py-2 rounded-lg">
          <img src={photo} alt="" className="h-12 w-12 rounded-lg object-cover" />
          <span className="text-sm text-[#0E3F80] flex-1">Image prête à envoyer</span>
          <button onClick={() => setPhoto(null)} className="text-xs text-slate-600 underline">Retirer</button>
        </div>
      )}

      {/* Composer */}
      <div className="flex items-center gap-2 pt-2 border-t border-[#E2E8F0]">
        <label className="p-2 rounded-lg hover:bg-[#EAF3FF] text-slate-600 cursor-pointer">
          <ImageIcon size={18} />
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="chat-image-input" />
        </label>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Écrivez un message…"
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
          className="flex-1"
          data-testid="chat-input"
        />
        <Button onClick={send} className="bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="chat-send-btn">
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
};
